import { parseTypedAmount } from '@/lib/number';
import type { StockingDetails } from '@/types/generated/openapi';

/**
 * The one place the stocking-sheet labels live. The average count, containers, container type and
 * CV% are stored as typed in one object with no maths on them; settling what they mean is a label
 * change here, not a migration.
 */
export const STOCKING_LABELS = {
  pieces_ordered: 'Ordered',
  ordered_allowance_pct: 'Allowance %',
  avg_count: 'Average count',
  containers: 'Containers',
  container_type: 'Container type',
  cv_pct: 'CV%',
} as const;

export type StockingFieldKey = keyof typeof STOCKING_LABELS;
export type StockingFieldText = Record<StockingFieldKey, string>;

export const EMPTY_STOCKING_TEXT: StockingFieldText = {
  pieces_ordered: '',
  ordered_allowance_pct: '',
  avg_count: '',
  containers: '',
  container_type: '',
  cv_pct: '',
};

const INT32 = 2147483647;

const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function stockingTextFrom(details: StockingDetails | null | undefined): StockingFieldText {
  const extras = (details?.stocking_extras ?? {}) as Record<string, unknown>;
  return {
    pieces_ordered: asText(details?.pieces_ordered),
    ordered_allowance_pct: details?.ordered_allowance_pct == null ? '' : String(Number(details.ordered_allowance_pct)),
    avg_count: asText(extras.avg_count),
    containers: asText(extras.containers),
    container_type: typeof extras.container_type === 'string' ? extras.container_type : '',
    cv_pct: asText(extras.cv_pct),
  };
}

export interface StockingPayload {
  pieces_ordered: number | null;
  ordered_allowance_pct: number | null;
  stocking_extras: Record<string, number | string>;
}

/** The typed fields as the API takes them, or a refusal per field. Blank is "not given". */
export function parseStockingText(t: StockingFieldText): { payload: StockingPayload; errors: Partial<Record<StockingFieldKey, string>> } {
  const errors: Partial<Record<StockingFieldKey, string>> = {};
  const whole = (key: StockingFieldKey, min: number): number | null => {
    const r = parseTypedAmount(t[key], { integer: true });
    if ('error' in r) {
      errors[key] = r.error;
      return null;
    }
    if (r.value === null) return null;
    if (r.value < min || r.value > INT32) errors[key] = min > 0 ? 'Whole number above 0.' : 'Whole number, 0 or more.';
    return errors[key] ? null : r.value;
  };
  const percent = (key: StockingFieldKey): number | null => {
    const r = parseTypedAmount(t[key]);
    if ('error' in r) {
      errors[key] = r.error;
      return null;
    }
    if (r.value === null) return null;
    if (r.value < 0 || r.value > 100) errors[key] = 'Between 0 and 100.';
    else if (Math.round(r.value * 100) / 100 !== r.value) errors[key] = 'At most 2 decimals.';
    return errors[key] ? null : r.value;
  };
  const payload: StockingPayload = {
    pieces_ordered: whole('pieces_ordered', 1),
    ordered_allowance_pct: percent('ordered_allowance_pct'),
    stocking_extras: {},
  };
  const avg = whole('avg_count', 0);
  const containers = whole('containers', 0);
  const cv = percent('cv_pct');
  const type = t.container_type.trim();
  if (type.length > 40) errors.container_type = 'At most 40 characters.';
  if (avg !== null) payload.stocking_extras.avg_count = avg;
  if (containers !== null) payload.stocking_extras.containers = containers;
  if (type && !errors.container_type) payload.stocking_extras.container_type = type;
  if (cv !== null) payload.stocking_extras.cv_pct = cv;
  return { payload, errors };
}

/** Live preview while typing; after saving, the figures come from the server's derived values. */
export function previewFigures(count: number | null, ordered: number | null, allowance: number | null, areaM2: number | null) {
  const extra = count !== null && ordered !== null ? count - ordered : null;
  return {
    extra,
    extraPct: extra !== null && ordered ? (extra * 100) / ordered : null,
    withAllowance: ordered !== null && allowance !== null ? Math.round(ordered * (1 + allowance / 100)) : null,
    density: count !== null && areaM2 ? count / areaM2 : null,
  };
}

export const fmtInt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());
/** The farm's density display everywhere: one decimal ("30.6"), never two. */
export const densityText = (n: number | string) => Number(n).toFixed(1);
export const fmtDensity = (n: number | string | null | undefined) =>
  n === null || n === undefined ? '—' : `${densityText(n)} / m²`;
export const fmtSigned = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${n > 0 ? '+' : ''}${n.toLocaleString()}`);
